import type {
  AttendanceToday,
  ChampActivityItem,
  ChampLoginState,
  ChampProfile,
  ChampRole,
  ChampStatus,
} from "@/api/champs";
import type { StatusTone } from "@/components/shared/status-badge";
import { activityLine as profileActivityLine } from "../profile/display";

/** How each champ status reads, and its tone — the one mapping. */
export const CHAMP_STATUS: Record<ChampStatus, { label: string; tone: StatusTone }> = {
  prospect: { label: "Prospect", tone: "neutral" },
  in_training: { label: "In training", tone: "info" },
  missed_training: { label: "Missed training", tone: "warning" },
  active: { label: "Active", tone: "success" },
  inactive: { label: "Inactive", tone: "neutral" },
};

/** The one badge: an area manager shows as that, whatever his status. */
export function champBadge(profile: Pick<ChampProfile, "role" | "status">): {
  label: string;
  tone: StatusTone;
} {
  if (profile.role === "area_manager") return { label: "Area manager", tone: "info" };
  return CHAMP_STATUS[profile.status];
}

export const ROLE_LABEL: Record<ChampRole, string> = {
  champ: "Champ",
  area_manager: "Area manager",
};

export const LOGIN_STATE: Record<ChampLoginState, { label: string; tone: StatusTone }> = {
  allowed: { label: "Login allowed", tone: "success" },
  blocked_otp: { label: "Blocked — OTP tries", tone: "warning" },
  blocked_admin: { label: "Blocked by admin", tone: "destructive" },
};

export const ATTENDANCE_WORD: Record<AttendanceToday, string> = {
  absent: "Absent",
  leave: "On leave",
  job_done: "Jobs done",
  marked: "Marked",
  pending: "Not marked yet",
};

/** "CH1234" — how staff say a champ number; "CH-12" when he has no code. */
export const champCode = (id: number, code: string | null): string =>
  code && code.trim() ? code : `CH-${id}`;

/** A language code as a word ("kn" → "Kannada"); the code itself when unknown. */
export function languageName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** One line for an activity item, for the Overview's compact list. */
export const activityLine = (item: ChampActivityItem): string =>
  profileActivityLine(item, "Champ");
